/*----------------------------------
- ASK AI SEARCH FILTERS
----------------------------------*/

/**
 * Pure helpers that translate decoded radar filters into the shared payload and
 * detect whether any Ask-AI search filter is active.
 *
 * Radar rule trees reach the client from SSR/URL decoding and can be malformed
 * (for example a group node whose nested `rules` array is missing). Every array
 * access below is guarded so a bad shape yields empty filters instead of
 * throwing during SSR render.
 */

import type {
    RadarRuleCondition as SharedRadarRuleCondition,
} from '@uniquedomains/components/radar/rules/types';
import type { DomainsRadarFiltersPayload } from '@uniquedomains/components/radar/utils/toDomainsRadarFiltersPayload';
import type {
    RadarRuleGroup as LandingRadarRuleGroup,
    RadarRuleNode as LandingRadarRuleNode,
    TInputRadar,
} from '@/common/types/domains/radar';
import type { WebsiteDomainsResultsSort } from './lib/resultsTableSort';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const EMPTY_SHARED_RULES: SharedRadarRuleCondition[] = [];

/*----------------------------------
- HELPERS
----------------------------------*/

export const toSharedRadarRuleConditions = (node: LandingRadarRuleNode): SharedRadarRuleCondition[] => {
    if ('rules' in node) {
        return Array.isArray(node.rules) ? node.rules.flatMap(toSharedRadarRuleConditions) : EMPTY_SHARED_RULES;
    }

    return [{
        filterId: node.filterId?.trim() || node.field,
        field: node.field,
        op: node.op,
        value: node.value,
    }];
};

export const toSharedRadarRules = (rules: LandingRadarRuleGroup | undefined): SharedRadarRuleCondition[] =>
    rules && Array.isArray(rules.rules) ? rules.rules.flatMap(toSharedRadarRuleConditions) : EMPTY_SHARED_RULES;

export const toSharedDomainsRadarFilters = (filters: Partial<TInputRadar>): DomainsRadarFiltersPayload => ({
    prompt: filters.prompt?.trim() || undefined,
    sort: filters.sort,
    rules: toSharedRadarRules(filters.rules),
});

export const hasActiveAskAISearchFilters = (
    filters: Partial<TInputRadar>,
    filterChips: Array<{ id: string }>,
    sort: WebsiteDomainsResultsSort | null,
): boolean =>
    Boolean(filters.prompt?.trim() || filters.rules?.rules?.length || filterChips.length || sort);
